import { useState, useEffect, useCallback } from 'react';
import { Plus, ChevronDown, ChevronUp, Trash2, CheckCircle2, Circle, Clock, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { API } from '@/lib/api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const ESTADO_CONFIG = {
  pendiente:   { label: 'Pendiente',   icon: Circle,       class: 'bg-slate-100 text-slate-600 border-slate-200' },
  en_proceso:  { label: 'En proceso',  icon: Clock,        class: 'bg-amber-50 text-amber-700 border-amber-200' },
  completado:  { label: 'Completado',  icon: CheckCircle2, class: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};

const PLAN_ESTADO = {
  activo:     'bg-blue-50 text-blue-700 border-blue-200',
  completado: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  cancelado:  'bg-slate-100 text-slate-500 border-slate-200',
};

const ZONAS = ['vestibular', 'lingual', 'mesial', 'distal', 'oclusal'];

function ProcedimientoRow({ proc, planId, pacienteId, onUpdate }) {
  const cfg = ESTADO_CONFIG[proc.estado] ?? ESTADO_CONFIG.pendiente;
  const Icon = cfg.icon;

  const siguiente = { pendiente: 'en_proceso', en_proceso: 'completado', completado: 'pendiente' };

  async function avanzarEstado() {
    try {
      const next = siguiente[proc.estado];
      await API.patch(`/patients/${pacienteId}/tratamientos/${planId}/procedimientos/${proc.id}`, { estado: next });
      onUpdate();
    } catch {
      toast.error('Error al actualizar procedimiento');
    }
  }

  return (
    <div className="flex items-start gap-3 py-2.5 px-3 rounded-lg hover:bg-muted/40 group">
      <button onClick={avanzarEstado} className="mt-0.5 shrink-0 text-muted-foreground hover:text-primary transition-colors">
        <Icon className="h-4 w-4" />
      </button>
      <div className="flex-1 min-w-0">
        <p className={cn('text-sm font-medium', proc.estado === 'completado' && 'line-through text-muted-foreground')}>
          {proc.descripcion}
        </p>
        {(proc.diente || proc.zona) && (
          <p className="text-xs text-muted-foreground">
            {proc.diente && `Diente ${proc.diente}`}{proc.diente && proc.zona && ' · '}{proc.zona}
          </p>
        )}
        {proc.notas && <p className="text-xs text-muted-foreground mt-0.5 italic">{proc.notas}</p>}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {proc.costo != null && (
          <span className="text-xs font-medium text-muted-foreground">
            ${proc.costo.toLocaleString('es-MX')}
          </span>
        )}
        <Badge variant="outline" className={cn('text-xs', cfg.class)}>{cfg.label}</Badge>
      </div>
    </div>
  );
}

function NuevoProcedimientoForm({ onGuardar, onCancelar }) {
  const [form, setForm] = useState({ descripcion: '', diente: '', zona: '', costo: '', notas: '' });

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  function handleGuardar() {
    if (!form.descripcion.trim()) { toast.error('La descripción es obligatoria'); return; }
    onGuardar({
      descripcion: form.descripcion.trim(),
      diente:      form.diente ? parseInt(form.diente) : null,
      zona:        form.zona || null,
      costo:       form.costo ? parseFloat(form.costo) : null,
      notas:       form.notas.trim() || null,
      estado:      'pendiente',
    });
  }

  return (
    <div className="border border-border/60 rounded-lg p-3 mt-2 bg-muted/30 space-y-2">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div className="sm:col-span-2">
          <Label className="text-xs">Procedimiento *</Label>
          <Input className="h-8 text-sm mt-1" placeholder="Ej: Extracción diente 38" value={form.descripcion} onChange={e => set('descripcion', e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">Diente (número)</Label>
          <Input className="h-8 text-sm mt-1" type="number" min={1} max={52} placeholder="Ej: 38" value={form.diente} onChange={e => set('diente', e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">Zona</Label>
          <Select value={form.zona} onValueChange={v => set('zona', v)}>
            <SelectTrigger className="h-8 text-sm mt-1"><SelectValue placeholder="Zona (opcional)" /></SelectTrigger>
            <SelectContent>{ZONAS.map(z => <SelectItem key={z} value={z}>{z}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Costo ($)</Label>
          <Input className="h-8 text-sm mt-1" type="number" min={0} placeholder="Ej: 1500" value={form.costo} onChange={e => set('costo', e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">Notas</Label>
          <Input className="h-8 text-sm mt-1" placeholder="Opcional" value={form.notas} onChange={e => set('notas', e.target.value)} />
        </div>
      </div>
      <div className="flex gap-2 justify-end pt-1">
        <Button size="sm" variant="ghost" onClick={onCancelar}><X className="h-3.5 w-3.5 mr-1" />Cancelar</Button>
        <Button size="sm" onClick={handleGuardar}><Plus className="h-3.5 w-3.5 mr-1" />Agregar</Button>
      </div>
    </div>
  );
}

function PlanCard({ plan, pacienteId, onUpdate, onDelete }) {
  const [expanded, setExpanded] = useState(true);
  const [agregando, setAgregando] = useState(false);

  const total = plan.procedimientos.reduce((s, p) => s + (p.costo ?? 0), 0);
  const completados = plan.procedimientos.filter(p => p.estado === 'completado').length;
  const progreso = plan.procedimientos.length ? Math.round((completados / plan.procedimientos.length) * 100) : 0;
  const pendientePago = Math.max(0, total - (plan.monto_pagado ?? 0));

  async function agregarProcedimiento(proc) {
    try {
      const nuevos = [...plan.procedimientos, proc];
      await API.put(`/patients/${pacienteId}/tratamientos/${plan.id}`, { ...plan, procedimientos: nuevos });
      setAgregando(false);
      onUpdate();
    } catch {
      toast.error('Error al agregar procedimiento');
    }
  }

  async function eliminarProcedimiento(procId) {
    try {
      const nuevos = plan.procedimientos.filter(p => p.id !== procId);
      await API.put(`/patients/${pacienteId}/tratamientos/${plan.id}`, { ...plan, procedimientos: nuevos });
      onUpdate();
    } catch {
      toast.error('Error al eliminar procedimiento');
    }
  }

  return (
    <Card className="border-border/50">
      <CardHeader className="pb-3 cursor-pointer" onClick={() => setExpanded(e => !e)}>
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <CardTitle className="text-base font-semibold">{plan.nombre}</CardTitle>
              <Badge variant="outline" className={cn('text-xs', PLAN_ESTADO[plan.estado] ?? '')}>{plan.estado}</Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">Inicio: {plan.fecha_inicio}</p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive hover:text-destructive"
              onClick={e => { e.stopPropagation(); onDelete(plan.id); }}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
            {expanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
          </div>
        </div>

        {/* Progreso */}
        {plan.procedimientos.length > 0 && (
          <div className="mt-3 space-y-1.5">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{completados}/{plan.procedimientos.length} procedimientos completados</span>
              <span>{progreso}%</span>
            </div>
            <div className="h-1.5 bg-muted rounded-full overflow-hidden">
              <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${progreso}%` }} />
            </div>
          </div>
        )}

        {/* Resumen financiero */}
        {total > 0 && (
          <div className="flex gap-4 mt-2 text-xs" onClick={e => e.stopPropagation()}>
            <span className="text-muted-foreground">Total: <span className="font-medium text-foreground">${total.toLocaleString('es-MX')}</span></span>
            <span className="text-muted-foreground">Pagado: <span className="font-medium text-emerald-600">${(plan.monto_pagado ?? 0).toLocaleString('es-MX')}</span></span>
            {pendientePago > 0 && <span className="text-muted-foreground">Pendiente: <span className="font-medium text-amber-600">${pendientePago.toLocaleString('es-MX')}</span></span>}
          </div>
        )}
      </CardHeader>

      {expanded && (
        <CardContent className="pt-0 space-y-1">
          {plan.procedimientos.length === 0 && !agregando && (
            <p className="text-sm text-muted-foreground text-center py-4">Sin procedimientos aún</p>
          )}
          {plan.procedimientos.map(proc => (
            <div key={proc.id} className="group relative">
              <ProcedimientoRow proc={proc} planId={plan.id} pacienteId={pacienteId} onUpdate={onUpdate} />
              <button
                onClick={() => eliminarProcedimiento(proc.id)}
                className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          {agregando
            ? <NuevoProcedimientoForm onGuardar={agregarProcedimiento} onCancelar={() => setAgregando(false)} />
            : (
              <Button size="sm" variant="outline" className="w-full mt-2 border-dashed" onClick={() => setAgregando(true)}>
                <Plus className="h-3.5 w-3.5 mr-1" />Agregar procedimiento
              </Button>
            )
          }
        </CardContent>
      )}
    </Card>
  );
}

function NuevoPlanForm({ pacienteId, onGuardar, onCancelar }) {
  const [nombre, setNombre] = useState('Plan de tratamiento');
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));

  async function handleGuardar() {
    if (!nombre.trim()) { toast.error('El nombre es obligatorio'); return; }
    try {
      await API.post(`/patients/${pacienteId}/tratamientos`, {
        nombre: nombre.trim(), fecha_inicio: fecha, estado: 'activo', procedimientos: [], monto_pagado: 0,
      });
      onGuardar();
    } catch {
      toast.error('Error al crear el plan');
    }
  }

  return (
    <Card className="border-primary/30 bg-primary/5">
      <CardContent className="pt-4 space-y-3">
        <div>
          <Label className="text-xs">Nombre del plan</Label>
          <Input className="h-8 text-sm mt-1" value={nombre} onChange={e => setNombre(e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">Fecha de inicio</Label>
          <Input className="h-8 text-sm mt-1" type="date" value={fecha} onChange={e => setFecha(e.target.value)} />
        </div>
        <div className="flex gap-2 justify-end">
          <Button size="sm" variant="ghost" onClick={onCancelar}><X className="h-3.5 w-3.5 mr-1" />Cancelar</Button>
          <Button size="sm" onClick={handleGuardar}><Plus className="h-3.5 w-3.5 mr-1" />Crear plan</Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function TratamientoDental({ patient }) {
  const [planes, setPlanes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creando, setCreando] = useState(false);

  const fetchPlanes = useCallback(async () => {
    try {
      const data = await API.get(`/patients/${patient.id}/tratamientos`);
      setPlanes(data);
    } catch {
      toast.error('Error al cargar planes de tratamiento');
    } finally {
      setLoading(false);
    }
  }, [patient.id]);

  useEffect(() => { fetchPlanes(); }, [fetchPlanes]);

  async function eliminarPlan(planId) {
    try {
      await API.delete(`/patients/${patient.id}/tratamientos/${planId}`);
      toast.success('Plan eliminado');
      fetchPlanes();
    } catch {
      toast.error('Error al eliminar el plan');
    }
  }

  if (loading) return <div className="text-sm text-muted-foreground p-4">Cargando planes...</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-base">Planes de Tratamiento</h3>
        {!creando && (
          <Button size="sm" onClick={() => setCreando(true)}>
            <Plus className="h-4 w-4 mr-1" />Nuevo plan
          </Button>
        )}
      </div>

      {creando && (
        <NuevoPlanForm
          pacienteId={patient.id}
          onGuardar={() => { setCreando(false); fetchPlanes(); }}
          onCancelar={() => setCreando(false)}
        />
      )}

      {planes.length === 0 && !creando && (
        <Card className="border-border/50">
          <CardContent className="py-10 text-center">
            <p className="text-muted-foreground text-sm">No hay planes de tratamiento registrados.</p>
            <Button size="sm" className="mt-3" onClick={() => setCreando(true)}>
              <Plus className="h-4 w-4 mr-1" />Crear primer plan
            </Button>
          </CardContent>
        </Card>
      )}

      {planes.map(plan => (
        <PlanCard
          key={plan.id}
          plan={plan}
          pacienteId={patient.id}
          onUpdate={fetchPlanes}
          onDelete={eliminarPlan}
        />
      ))}
    </div>
  );
}
